import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, Blason } from "@/components/Logo";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const homeFor = (r) => (r === "chauffeur" ? "/driver" : r === "superadmin" ? "/super" : "/app");

// Activation d'un espace utilisateur depuis un lien d'invitation (jeton unique, expirant).
export default function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Lien d'invitation incomplet."); return; }
    api.get("/auth/invitation", { params: { token } }).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e.response?.data?.detail) || e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (pw !== pw2) return setError("Les deux mots de passe ne correspondent pas.");
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/invitation/accept", { token, password: pw });
      setSession?.(data);
      nav(homeFor(data.user.role), { replace: true });
    } catch (err) { setError(apiError(err.response?.data?.detail) || err.message); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-900 flex items-center justify-center p-4" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
      <div data-testid="invite-page" className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="flex justify-center mb-6"><Blason size={150} /></div>
        {!info && !error && <p className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Vérification de l'invitation…</p>}
        {error && !info && (
          <div data-testid="invite-error" className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800 flex gap-2"><AlertTriangle className="w-5 h-5 shrink-0" /><div>{error}<p className="mt-2"><Link to="/login" className="font-semibold underline">Aller à la connexion</Link></p></div></div>
        )}
        {info && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h1 className="text-2xl font-heading font-extrabold text-brand-900">Bienvenue {info.first_name}</h1>
              <p className="text-slate-500 text-sm mt-1">Vous rejoignez <b>{info.organization_name}</b>. Choisissez votre mot de passe pour activer votre espace.</p>
            </div>
            <div><Label>Email</Label><Input data-testid="invite-email-readonly" value={info.email} readOnly name="email" autoComplete="username" className="mt-1 h-11 bg-slate-50" /></div>
            <div><Label>Mot de passe</Label><Input data-testid="invite-password" type="password" name="new-password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 h-11" placeholder="8 caractères minimum" /></div>
            <div><Label>Confirmer le mot de passe</Label><Input data-testid="invite-password2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 h-11" /></div>
            {error && <p data-testid="invite-form-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="invite-activate" type="submit" disabled={busy} className="w-full h-12 bg-accent-600 hover:bg-accent-700 font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}Activer mon espace</Button>
            <p className="text-xs text-slate-400">Lien personnel, valable une seule fois.</p>
          </form>
        )}
      </div>
    </div>
  );
}
